export interface AgentFeature {
    title: string;
    description: string;
}

export interface AgentSummary {
    id: string;
    name: string;
    colorClass: string;
    tooltipTitle: string;
    path: string;
    role: string;
    tagline: string;
    description: string;
    features: AgentFeature[];
}

export const AGENTS: AgentSummary[] = [
    {
        id: "nova",
        name: "Nova",
        colorClass: "bg-gradient-to-br from-blue-500 to-blue-700",
        tooltipTitle: "Nova — Sales Agent",
        path: "/nova",
        role: "Sales Agent",
        tagline: "qualifies leads & books demos",
        description:
            "Nova follows up with every new lead the moment they come in, qualifies them over chat or phone, and gets qualified prospects straight onto your calendar.",
        features: [
            { title: "Instant lead follow-up", description: "Responds to new leads in seconds, day or night" },
            { title: "Books demos", description: "Finds a slot and confirms it without back-and-forth" },
            { title: "Qualifies prospects", description: "Asks the right questions before handing off" },
            { title: "CRM sync", description: "Logs every conversation to your pipeline automatically" },
        ],
    },
    {
        id: "ember",
        name: "Ember",
        colorClass: "bg-gradient-to-br from-orange-500 to-red-600",
        tooltipTitle: "Ember — Retention Agent",
        path: "/ember",
        role: "Retention Agent",
        tagline: "identifies at-risk members & saves cancellations",
        description:
            "Ember is the retention agent for Antares — it identifies members at risk of leaving, intervenes with the right action at the right time, and engages directly when a member chooses to cancel.",
        features: [
            { title: "Flags at-risk members early", description: "Surfaces members drifting into amber on the Keepme Score, with weeks of runway" },
            { title: "Engages at the right moment", description: "Reaches out via WhatsApp, in-app or at check-in with a relevant offer" },
            { title: "Handles cancellation requests", description: "Steps in on the cancellation flow with freezes, transfers, or the best save" },
            { title: "Feeds retention insights", description: "Captures the real cancellation reason and feeds it back into Nova's lead qualification" },
        ],
    },
    {
        id: "atlas",
        name: "Atlas",
        colorClass: "bg-gradient-to-br from-emerald-400 to-emerald-600",
        tooltipTitle: "Atlas — Member Support Agent",
        path: "/atlas",
        role: "Member Services Agent",
        tagline: "answers member questions & takes action 24/7",
        description:
            "Atlas is the member services agent for Antares — it lives inside your member app and members' area, answering questions and taking action for authenticated members in seconds, day or night.",
        features: [
            { title: "Answers member questions", description: "Instantly answers billing, bookings, facilities and class questions in your brand voice" },
            { title: "Takes permitted actions", description: "Books classes, updates plans and applies changes your systems allow" },
            { title: "Smart escalation", description: "Routes anything sensitive to the right person with full context" },
            { title: "Built for authenticated members", description: "Works inside your member app, with access to each member's own record" },
        ],
    },
    {
        id: "clarion",
        name: "Clarion",
        colorClass: "bg-gradient-to-br from-violet-500 to-purple-700",
        tooltipTitle: "Clarion — Voice Agent",
        path: "/clarion",
        role: "Voice Agent",
        tagline: "answers & places calls",
        description:
            "Clarion answers your phones around the clock — so no enquiry ever rings out. It picks up inbound calls, books tours by phone, confirms appointments, and takes messages after hours, all in a natural voice that sounds like your front desk.",
        features: [
            { title: "Never miss a call", description: "Answers inbound calls 24/7, including out of hours" },
            { title: "Books tours by phone", description: "Offers slots and confirms the time out loud" },
            { title: "Confirms appointments", description: "Outbound reminder calls for PT and tours" },
            { title: "Takes messages", description: "Captures name, number and reason for callback" },
        ],
    },
    {
        id: "beacon",
        name: "Beacon",
        colorClass: "bg-gradient-to-br from-orange-400 to-amber-600",
        tooltipTitle: "Beacon",
        path: "/beacon",
        role: "AEO Agent",
        tagline: "gets you recommended by AI search",
        description:
            "Beacon optimizes how your brand shows up when people ask AI assistants and search engines for recommendations, so you're the answer they give.",
        features: [
            { title: "AI visibility tracking", description: "Monitors how AI engines describe your brand" },
            { title: "Content recommendations", description: "Tells you what to publish to get cited" },
            { title: "Competitor benchmarking", description: "Shows how you compare in AI answers" },
            { title: "Implementation help", description: "Walks your team through each fix" },
        ],
    },
];

export const getAgentImageSrc = (agent: Pick<AgentSummary, "name">) =>
    `/${agent.name.toLowerCase()}.png`;

export const getAgentLetter = (agent: Pick<AgentSummary, "name">) =>
    agent.name.charAt(0).toUpperCase();

const ACTIVE_AGENT_KEY = "activeAgentId";
export function setActiveAgent(agentId: string): void {
    if(agentId)
    localStorage.setItem(ACTIVE_AGENT_KEY, agentId.toLowerCase());

}

export function getActiveAgent(): string | null {
    try {
        return localStorage.getItem(ACTIVE_AGENT_KEY);
    } catch (err) {
        return null;
    }
}

export function clearActiveAgent(): void {
    localStorage.removeItem(ACTIVE_AGENT_KEY);
}